/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import MainLayout from "../../../layouts/MainLayout";
import Form from "../../../components/forms/Form";
import PageHeader from "../../../components/PageHeader";
import { useLocation, useNavigate } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import { customerFields } from "../../../constants/forms/customerFields";
import useNotification from "../../../hooks/useNotification";
import useLoader from "../../../hooks/useLoader";
import { removeMask, removeMaskCpfCnpj } from "../../../utils/maskUtils";
import useForm from "../../../hooks/useForm";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";
import { useOrgan } from "../../../hooks/useOrgan";
import { useSectionFields } from "../../../hooks/useSectionFields";

const CreateCustomerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { showLoader, hideLoader } = useLoader();

  const queryParams = new URLSearchParams(location.search);

  const parentId = queryParams.get("parent_id");

  const {
    post: baseServicePost,
    get: baseServiceGet,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { selectedOrgan } = useOrgan();

  const { formData, handleChange, setFormData, resetForm } = useForm(
    setDefaultFieldValues(customerFields),
    [customerFields],
  );

  const [customerFieldState, setCustomerFieldState] = useState(customerFields);
  const { removeFields } = useSectionFields();

  useEffect(() => {
    let updatedFields = [...customerFields];
    if (!parentId) {
      updatedFields = removeFields({
        arrayDependente: updatedFields,
        field_id: ["customer.parent_id"],
      });
    }
    setCustomerFieldState(updatedFields);
  }, [parentId, removeFields]);

  const queryClient = useQueryClient();

  const handleCepChange = useCallback(
    async (e) => {
      const value = e.target.value;
      const zip = removeMask(value);

      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          zip: value,
        },
      }));

      if (zip.length === 8) {
        showLoader();
        try {
          const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
          if (!response.ok) throw new Error("Erro ao buscar o CEP");

          const data = await response.json();
          if (data.erro) throw new Error("CEP não encontrado");

          setFormData((prev) => ({
            ...prev,
            address: {
              ...prev.address,
              street: data.logradouro || "",
              district: data.bairro || "",
              city: data.localidade || "",
              state: data.estado || "",
              country: "Brasil",
            },
          }));

          showNotification("success", "Endereço preenchido automaticamente!");
        } catch (error) {
          showNotification("error", error.message);
        } finally {
          hideLoader();
        }
      }
    },
    [hideLoader, setFormData, showLoader, showNotification],
  );

  const handleFieldChange = useCallback(
    (fieldId, value) => {
      if (fieldId === "address.zip") {
        handleCepChange({ target: { value } });
      } else {
        handleChange(fieldId, value);
      }
    },
    [handleCepChange, handleChange],
  );

  const createCustomerMutation = useMutation({
    mutationFn: async (dataToCreate) => {
      showLoader();
      const response = await baseServicePost(
        entities.customers.create,
        dataToCreate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        resetForm();
        queryClient.invalidateQueries({ queryKey: ["customers"] });
        queryClient.invalidateQueries({ queryKey: ["customerOptions"] });
        queryClient.invalidateQueries({
          queryKey: ["recipientCustomerOptions"],
        });
        navigate("/aluguel/clientes", { state: { type: "success" } });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const customerParentSelect = useRemoteSelectOptions({
    queryKey: ["customers"],
    columnDataBase: ["id", "alias"],
    baseServiceGet,
    entityPath: entities.customers.get,
    extraFilters: {
      id: { $eq: parentId },
      organization_id: { $eq: selectedOrgan?.id },
      alias: { $nin: ["ALUCOM", "MOREIA", "IP", "AS SISTEMAS"] },
    },
    fieldId: "customer.parent_id",
    formData,
    isMulti: false,
    enabled: !!parentId,
    immediateFetch: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.alias}`,
    }),
    setFormData,
    perPage: 15,
    enablePagination: true,
  });

  useEffect(() => {
    if (customerParentSelect.rawData?.length) {
      const parentCustomer = customerParentSelect.rawData[0];

      setFormData((prev) => ({
        ...prev,
        customer: {
          ...prev.customer,
          parent_id: parentCustomer.alias,
        },
      }));
    }
  }, [customerParentSelect.rawData]);

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);

      const sanitizedData = {
        ...(formData?.customer && {
          customer: {
            ...cleanedData.customer,
            parent_id: customerParentSelect.rawData[0]?.id || null,
            cpf_cnpj: cleanedData.customer.cpf_cnpj
              ? String(removeMaskCpfCnpj(cleanedData.customer.cpf_cnpj))
              : null,
            phone: cleanedData.customer.phone ?? null,
            organization_id: selectedOrgan?.id,
          },
        }),
        ...(formData?.address && {
          address: cleanedData.address
            ? {
                ...cleanedData.address,
                number: cleanedData.address.number
                  ? String(cleanedData.address.number)
                  : null,
                zip: cleanedData.address.zip
                  ? removeMask(cleanedData.address.zip)
                  : null,
              }
            : null,
        }),
        ...(formData?.contact && {
          contact: cleanedData.contact
            ? {
                ...cleanedData.contact,
              }
            : null,
        }),
        ...(cleanedData?.location && Object.keys(cleanedData.location).length > 0 && {
          location: cleanedData.location,
        }),
      };

      // Remove propriedades undefined do objeto final
      const finalData = Object.fromEntries(
        Object.entries(sanitizedData).filter(([_, v]) => v !== undefined),
      );

      await createCustomerMutation.mutateAsync(finalData);
    } catch (error) {
      console.error("Erro ao cadastrar cliente:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, createCustomerMutation, selectedOrgan, showNotification]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Clientes"
        showBackButton={true}
        backUrl="/aluguel/clientes/"
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          textSubmit="Cadastrar"
          initialFormData={formData}
          textLoadingSubmit="Cadastrando..."
          handleBack={() => navigate("/aluguel/clientes")}>
          {() => {
            const renderedSections = new Set();

            return customerFieldState?.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleFieldChange}
                    getOptions={() => {}}
                    getSelectedValue={() => {}}
                    getSelectMeta={() => {}}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateCustomerPage;
